import { FC, useState } from "react";
import { useParams } from "react-router-dom";
import { Typography, Box, Checkbox, Button } from "@kamstrup/kfl";
import InfoBox from "../infoBox/InfoBox";
import { CenteredCard } from "../../containers/card";
import { useTranslation } from "../../hooks/useTranslationHook/useTranslation";
import { commonStyles } from "../../styles/commonStyles";
import {
  useInviteExistingUserFlow,
  InviteExistingUserStepEnum
} from "../../contexts/inviteExistingUserContext";
import { BusinessIcon } from "./businessIcon";
import { styles } from "./styles";
import { useInvitationChoice } from "../../hooks/useInvitationChoice/useInvitationChoice";
import { useToastNotification } from "../../hooks/useToastNotification/useToastNotification";
import { TranslationKeyType } from "../../hooks/useTranslationHook/types";

export const AcceptMoveStep: FC = () => {
  const { translateString } = useTranslation();
  const { workflowId } = useParams<{ workflowId: string }>();

  const {
    setMoveResult,
    setCurrentStep,
    organization,
    currentOrganization,
    userEmail,
    invitationState
  } = useInviteExistingUserFlow();

  const { sendInvitationChoice } = useInvitationChoice();
  const { showNotification, NotificationElement } = useToastNotification();

  const [isConfirmed, setIsConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);

  const goConfirmation = (result: "accepted" | "rejected") => {
    setMoveResult(result);
    setCurrentStep(InviteExistingUserStepEnum.ConfirmationStep);
  };

  const handleAccept = async () => {
    if (!organization || !workflowId) return;
    if (invitationState !== "Created") return;
    try {
      setBusy(true);
      const res = await sendInvitationChoice(workflowId, "accepted");
      if (!res.ok) {
        const text = res.code
          ? translateString(res.code as any)
          : (res.message ?? translateString("workflow_missing_error" as any));
        showNotification(text, "error");
        return;
      }
      goConfirmation("accepted");
    } finally {
      setBusy(false);
    }
  };

  const handleDecline = async () => {
    if (!workflowId) {
      goConfirmation("rejected");
      return;
    }
    if (invitationState !== "Created") return;
    try {
      setBusy(true);
      const res = await sendInvitationChoice(workflowId, "rejected");
      if (!res.ok) {
        const text = res.code
          ? translateString(res.code as any)
          : (res.message ?? translateString("workflow_missing_error" as any));
        showNotification(text, "error");
        return;
      }
      goConfirmation("rejected");
    } finally {
      setBusy(false);
    }
  };

  const isActionDisabled = invitationState !== "Created";

  const titleKey: TranslationKeyType = (() => {
    switch (invitationState) {
      case "Created":
        return "move_organization";
      case "Completed":
        return "invitation_already_accepted";
      case "Cancelled":
        return "invitation_cancelled";
      case "Expired":
        return "invitation_expired";
      default:
        return "invitation_not_found";
    }
  })();

  const descriptionKey: TranslationKeyType = (() => {
    switch (invitationState) {
      case "Created":
        return "move_organization_intro";
      case "Completed":
        return "invitation_already_accepted_description";
      case "Cancelled":
        return "invitation_cancelled_description";
      case "Expired":
        return "invitation_expired_description";
      default:
        return "invitation_not_found_description";
    }
  })();

  return (
    <CenteredCard title={translateString(titleKey)}>
      {invitationState === "Created" ? (
        <Typography variant="body4" sx={commonStyles.introText}>
          {translateString("move_organization_intro")} {userEmail}{" "}
          {translateString("move_organization_intro_2")}
        </Typography>
      ) : (
        <Typography variant="body4" sx={commonStyles.introText}>
          {translateString(descriptionKey)}
        </Typography>
      )}

      <Typography variant="body4" fontWeight="bold" sx={{ marginBottom: "4px" }}>
        {translateString("current_organization_label")}
      </Typography>
      <Box sx={styles.organizationBox}>
        <Box sx={styles.iconContainer}>
          <BusinessIcon />
        </Box>
        <Typography variant="body4">
          {currentOrganization || translateString("unknown_organization")}
        </Typography>
      </Box>

      <Typography variant="body4" fontWeight="bold" sx={{ marginBottom: "4px" }}>
        {translateString("new_organization_label")}
      </Typography>
      <Box sx={styles.organizationBox}>
        <Box sx={styles.iconContainer}>
          <BusinessIcon />
        </Box>
        <Typography variant="body4">
          {organization || translateString("unknown_organization")}
        </Typography>
      </Box>

      {invitationState === "Created" && (
        <>
          <Box>
            <InfoBox type="error">{translateString("move_new_organization_info")}</InfoBox>
          </Box>

          <Box sx={styles.checkBoxBox}>
            <Checkbox checked={isConfirmed} onChange={(e) => setIsConfirmed(e.target.checked)} />
            <Typography variant="body3" sx={{ marginTop: "12px" }}>
              {translateString("move_organization_checkbox")}
            </Typography>
          </Box>
        </>
      )}

      <Box sx={commonStyles.buttonContainer}>
        <Button
          color="primary"
          variant="contained"
          fullWidth
          sx={commonStyles.button}
          onClick={handleAccept}
          disabled={!isConfirmed || busy || isActionDisabled}
        >
          <Typography variant="body4">{translateString("accept_move")}</Typography>
        </Button>
      </Box>

      <Box sx={commonStyles.buttonContainer}>
        <Button
          color="primary"
          variant="outlined"
          fullWidth
          sx={commonStyles.button}
          onClick={handleDecline}
          disabled={busy || isActionDisabled}
        >
          <Typography variant="body4">{translateString("reject_move")}</Typography>
        </Button>
      </Box>

      {NotificationElement}
    </CenteredCard>
  );
};
